import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navigation } from './Navigation';
import './Dashboard.css';
import './FourBucketExplainer.css';

/* ── Data ── */

const BUCKETS = [
  {
    num: 1, name: 'Cash',   timeline: 'Year 1',     pct: 5,
    purpose: 'Spending money for next 12 months',
    investments: 'HYSA, Money Market',
    risk: 'Zero Risk',
    bg: '#D1FAE5', text: '#276749', border: '#6ee7b7',
  },
  {
    num: 2, name: 'Buffer', timeline: 'Years 2–3',  pct: 10,
    purpose: 'Refill Bucket 1 annually',
    investments: 'SCHD, Short-term bonds, CDs, I-Bonds',
    risk: 'Low Risk',
    bg: '#DBEAFE', text: '#1e40af', border: '#93c5fd',
  },
  {
    num: 3, name: 'Bridge', timeline: 'Years 4–10', pct: 27,
    purpose: 'Bridge through market crashes',
    investments: 'Balanced funds (60/40), VIG, Target-date funds',
    risk: 'Moderate Risk',
    bg: '#FEF3C7', text: '#92400e', border: '#fcd34d',
  },
  {
    num: 4, name: 'Growth', timeline: 'Years 10+',  pct: 58,
    purpose: 'Long-term wealth, beat inflation',
    investments: 'VOO, VTI, Growth index funds',
    risk: 'Growth Risk',
    bg: '#E9D8FD', text: '#5b21b6', border: '#c4b5fd',
  },
] as const;

const STEPS = [
  { num: 1, label: 'What' },
  { num: 2, label: 'Why'  },
  { num: 3, label: 'When' },
  { num: 4, label: 'How'  },
  { num: 5, label: 'Ready' },
];

function fmt(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function calcBuckets(savings: number, expenses: number) {
  const amounts = [savings * 0.05, savings * 0.10, savings * 0.27, savings * 0.58];
  return { amounts, runway: (amounts[0] + amounts[1] + amounts[2]) / expenses };
}

/* ── Step Components ── */

const StepWhat: React.FC = () => (
  <div>
    <p className="fbe-label">Step 1 of 5</p>
    <h2 className="fbe-title">The 4 Buckets</h2>
    <p className="fbe-intro">
      Your savings split into four buckets, each covering a different slice of time.
      Think of it as a relay race — each bucket hands off to the next so your
      growth investments always have a decade to recover from any crash.
    </p>

    <div className="fbe-buckets-grid">
      {BUCKETS.map(b => (
        <div key={b.num} className="fbe-bucket-card" style={{ borderTopColor: b.border, backgroundColor: b.bg }}>
          <div className="fbe-bucket-header">
            <span className="fbe-bucket-circle" style={{ backgroundColor: b.text }}>{b.num}</span>
            <div className="fbe-bucket-name-wrap">
              <div className="fbe-bucket-name"    style={{ color: b.text }}>{b.name}</div>
              <div className="fbe-bucket-timeline" style={{ color: b.text }}>{b.timeline}</div>
            </div>
            <div className="fbe-bucket-pct" style={{ color: b.text }}>{b.pct}%</div>
          </div>
          <div className="fbe-bucket-bar-track">
            <div className="fbe-bucket-bar-fill" style={{ width: `${b.pct * 1.5}%`, backgroundColor: b.text }} />
          </div>
          <p className="fbe-bucket-purpose" style={{ color: b.text }}>{b.purpose}</p>
          <div className="fbe-bucket-inv">
            <strong>Investments: </strong>{b.investments}
          </div>
          <div className="fbe-bucket-risk" style={{ color: b.text }}>
            <span className="fbe-risk-dot" style={{ backgroundColor: b.text }} />
            {b.risk}
          </div>
        </div>
      ))}
    </div>

    <div className="fbe-insight">
      <div className="fbe-insight-icon">💡</div>
      <p>
        <strong>Bucket 4 is still 58% — the majority.</strong> At 65 you might live to 95.
        That's 30 years needing growth. This system isn't about being conservative —
        it's about being <strong>strategic</strong>. You protect B1–B3 so B4 can do its
        job without being touched during a crash.
      </p>
    </div>
  </div>
);

const StepWhy: React.FC = () => (
  <div>
    <p className="fbe-label">Step 2 of 5</p>
    <h2 className="fbe-title">The Problem It Solves</h2>
    <p className="fbe-intro">
      <strong>Sequence of Returns Risk</strong> — when you're forced to sell during a crash,
      you lock in losses permanently. Your portfolio never fully recovers, even if the market does.
    </p>

    <div className="fbe-compare-grid">
      <div className="fbe-compare-card fbe-compare-bad">
        <div className="fbe-compare-head">
          <span>✗</span> Without 4-Bucket System
        </div>
        <ul className="fbe-compare-list">
          <li>$1M in a single account</li>
          <li>Market crashes 35%</li>
          <li>Portfolio drops to <strong>$650K</strong></li>
          <li>Need $50K to live on</li>
          <li className="fbe-bad">Forced to sell at the bottom</li>
          <li>Remaining: <strong>$600K</strong></li>
          <li className="fbe-bad">Losses locked in — portfolio crippled</li>
        </ul>
      </div>
      <div className="fbe-compare-card fbe-compare-good">
        <div className="fbe-compare-head">
          <span>✓</span> With 4-Bucket System
        </div>
        <ul className="fbe-compare-list">
          <li>$1M split: B1 $50K · B2 $100K · B3 $270K · B4 $580K</li>
          <li>Market crashes 35%</li>
          <li>B1 (cash): Still <strong>$50K — unaffected</strong></li>
          <li>B4 (growth): Drops to $377K — <strong>not touching it</strong></li>
          <li>Need $50K? Take it from B1</li>
          <li className="fbe-good">Zero selling at the bottom</li>
          <li className="fbe-good">B4 recovers fully with the market</li>
        </ul>
      </div>
    </div>

    <div className="fbe-math-panel">
      <div className="fbe-math-title">Crash Protection Math</div>
      <div className="fbe-math-sub">$1.5M portfolio · $60K/year expenses</div>
      <div className="fbe-runway-rows">
        {[
          { lbl: 'B1 — Cash',   calc: '$75K ÷ $60K =',  yrs: '1.25 yrs', color: '#276749', bg: '#D1FAE5' },
          { lbl: 'B2 — Buffer', calc: '$150K ÷ $60K =', yrs: '2.5 yrs',  color: '#1e40af', bg: '#DBEAFE' },
          { lbl: 'B3 — Bridge', calc: '$405K ÷ $60K =', yrs: '6.75 yrs', color: '#92400e', bg: '#FEF3C7' },
        ].map(r => (
          <div key={r.lbl} className="fbe-runway-row" style={{ backgroundColor: r.bg }}>
            <span className="fbe-runway-lbl"  style={{ color: r.color }}>{r.lbl}</span>
            <span className="fbe-runway-calc">{r.calc}</span>
            <span className="fbe-runway-yrs"  style={{ color: r.color }}>{r.yrs}</span>
          </div>
        ))}
        <div className="fbe-runway-total">
          <span>Total runway without touching B4</span>
          <span className="fbe-runway-total-val">10.5 years ✓</span>
        </div>
      </div>
      <div className="fbe-historical">
        {[
          { year: '2008 Crash',     recovery: 'Recovery: 5 years' },
          { year: '2000 Dot-Com',   recovery: 'Recovery: 7 years' },
          { year: 'Any Historical', recovery: 'Max: ~7 years'     },
        ].map(h => (
          <div key={h.year} className="fbe-hist-item">
            <div className="fbe-hist-year">{h.year}</div>
            <div className="fbe-hist-recovery">{h.recovery}</div>
            <div className="fbe-hist-ok">Runway: 10.5 yrs ✓</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const StepWhen: React.FC = () => (
  <div>
    <p className="fbe-label">Step 3 of 5</p>
    <h2 className="fbe-title">Is This Right For You?</h2>
    <p className="fbe-intro">
      Start building 5–10 years <strong>before</strong> you retire.
      The earlier you begin the transition, the smoother the shift from growth to distribution.
    </p>

    <div className="fbe-timeline">
      {[
        {
          phase: 'Accumulation', age: 'Age 20–50',
          title: 'Focus on Growth',
          detail: 'B1 (emergency fund) + B4 (growth) only. Add B2/B3 only for specific goals.',
          color: '#276749', bg: 'rgba(39,103,73,0.12)', border: 'rgba(110,231,183,0.3)',
        },
        {
          phase: 'Transition', age: 'Age 50–65',
          title: 'Start Building All 4',
          detail: 'Gradually shift contributions across all four buckets. B4 still gets the majority.',
          color: '#60a5fa', bg: 'rgba(30,64,175,0.12)', border: 'rgba(147,197,253,0.3)',
        },
        {
          phase: 'Distribution', age: 'Age 65+',
          title: 'System Fully Active',
          detail: 'Spend from B1, refill from B2–B3. Protect B4 during any downturn.',
          color: '#c4b5fd', bg: 'rgba(91,33,182,0.12)', border: 'rgba(196,181,253,0.3)',
        },
      ].map((t, i) => (
        <div key={i} className="fbe-tl-item">
          <div className="fbe-tl-dot" style={{ backgroundColor: t.color, boxShadow: `0 0 0 2px ${t.color}` }} />
          <div className="fbe-tl-card" style={{ backgroundColor: t.bg, borderColor: t.border }}>
            <div className="fbe-tl-phase"  style={{ color: t.color }}>{t.phase}</div>
            <div className="fbe-tl-age">{t.age}</div>
            <div className="fbe-tl-title">{t.title}</div>
            <div className="fbe-tl-detail">{t.detail}</div>
          </div>
        </div>
      ))}
    </div>

    <div className="fbe-table-title">Recommended Allocation by Age</div>
    <div className="fbe-table-wrap">
      <table className="fbe-age-table">
        <thead>
          <tr>
            <th>Age</th>
            <th style={{ color: '#6ee7b7' }}>B1 Cash</th>
            <th style={{ color: '#93c5fd' }}>B2 Buffer</th>
            <th style={{ color: '#fcd34d' }}>B3 Bridge</th>
            <th style={{ color: '#c4b5fd' }}>B4 Growth</th>
          </tr>
        </thead>
        <tbody>
          {[
            { age: '50–55', b: ['3%', '6%',  '22%', '69%'] },
            { age: '55–60', b: ['4%', '9%',  '26%', '61%'] },
            { age: '60–65', b: ['5%', '10%', '27%', '58%'] },
            { age: '65+',   b: ['5%', '10%', '27%', '58%'] },
          ].map(row => (
            <tr key={row.age}>
              <td>{row.age}</td>
              {row.b.map((v, i) => <td key={i}>{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="fbe-who-grid">
      <div className="fbe-who-card fbe-who-yes">
        <h4>This is for you if:</h4>
        <ul>
          <li>5–15 years from retirement</li>
          <li>Already retired, need to organize savings</li>
          <li>Worried about market crashes affecting withdrawals</li>
          <li>Want a clear system, not guesswork</li>
        </ul>
      </div>
      <div className="fbe-who-card fbe-who-wait">
        <h4>Wait if:</h4>
        <ul>
          <li>Under 45 with 20+ years to go — focus on growth</li>
          <li>Still building your emergency fund — fill B1 first</li>
        </ul>
      </div>
    </div>
  </div>
);

const StepHow: React.FC = () => {
  const [savings,  setSavings]  = useState('1200000');
  const [expenses, setExpenses] = useState('60000');
  const { isAuthenticated } = useAuth();

  const savingsNum  = parseFloat(savings)  || 0;
  const expensesNum = parseFloat(expenses) || 0;
  const calc = savingsNum > 0 && expensesNum > 0 ? calcBuckets(savingsNum, expensesNum) : null;

  return (
    <div>
      <p className="fbe-label">Step 4 of 5</p>
      <h2 className="fbe-title">Setting It Up</h2>
      <p className="fbe-intro">
        Five concrete steps to go from where you are now to a fully structured 4-Bucket System.
      </p>

      <div className="fbe-steps-list">
        {[
          {
            n: '01', title: 'Calculate Annual Core Expenses',
            desc: 'Housing, food, healthcare, utilities only. What you need — not what you spend.',
            example: '$5,000/month = $60,000/year core expenses',
            isMaint: false,
          },
          {
            n: '02', title: 'Calculate Bucket Sizes',
            desc: 'Apply the percentages to your total portfolio. Use the calculator below.',
            example: '$1.5M → B1: $75K · B2: $150K · B3: $405K · B4: $870K',
            isMaint: false,
          },
          {
            n: '03', title: 'Choose Investments for Each Bucket',
            desc: 'Match each bucket to investments suited to its time horizon.',
            example: 'B1: HYSA · B2: SCHD/CDs · B3: VIG/Balanced · B4: VOO/VTI',
            isMaint: false,
          },
          {
            n: '04', title: 'Reorganize Your Holdings',
            desc: 'Option A: Gradual (3–5 years, redirect contributions). Option B: One-time rebalance in tax-advantaged accounts.',
            example: 'Note: consult a tax advisor before selling in taxable accounts',
            isMaint: false,
          },
          { n: '05', title: 'Annual Maintenance', desc: '', example: '', isMaint: true },
        ].map((s, i) => (
          <div key={i} className="fbe-step">
            <div className="fbe-step-circle">{s.n}</div>
            <div className="fbe-step-body">
              <h3 className="fbe-step-title">{s.title}</h3>
              {s.isMaint ? (
                <div className="fbe-maint-grid">
                  <div className="fbe-maint-card fbe-maint-normal">
                    <h4>Normal Years</h4>
                    <ol>
                      <li>Spend from B1 monthly</li>
                      <li>January: Refill B1 from B2</li>
                      <li>Refill B2 from B3</li>
                      <li>Refill B3 from B4 (good years only)</li>
                    </ol>
                  </div>
                  <div className="fbe-maint-card fbe-maint-crash">
                    <h4>Crash Years (down 20%+)</h4>
                    <ol>
                      <li><strong>Stop all B4 withdrawals</strong></li>
                      <li>Continue spending from B1</li>
                      <li>Refill B1 from B2</li>
                      <li>Refill B2 from B3 if needed</li>
                      <li>Wait for B4 to recover (3–7 yrs)</li>
                      <li>Resume normal once recovered</li>
                    </ol>
                  </div>
                </div>
              ) : (
                <>
                  {s.desc && <p className="fbe-step-desc">{s.desc}</p>}
                  {s.example && <div className="fbe-step-example">{s.example}</div>}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Inline Calculator */}
      <div className="fbe-calc-box">
        <div className="fbe-calc-title">Quick Calculator</div>
        <div className="fbe-calc-sub">Enter your numbers to see your bucket sizes instantly.</div>
        <div className="fbe-calc-inputs">
          <label className="fbe-calc-label">
            Total Portfolio ($)
            <input type="number" className="fbe-calc-input" value={savings}  onChange={e => setSavings(e.target.value)}  placeholder="1200000" />
          </label>
          <label className="fbe-calc-label">
            Annual Core Expenses ($)
            <input type="number" className="fbe-calc-input" value={expenses} onChange={e => setExpenses(e.target.value) } placeholder="60000" />
          </label>
        </div>
        {calc && (
          <>
            <div className="fbe-calc-rows">
              {BUCKETS.map((b, i) => (
                <div key={b.num} className="fbe-calc-row" style={{ backgroundColor: b.bg, borderColor: b.border }}>
                  <div className="fbe-calc-row-name" style={{ color: b.text }}>
                    <strong>B{b.num} {b.name}</strong> ({b.pct}%)
                  </div>
                  <div className="fbe-calc-row-amt" style={{ color: b.text }}>{fmt(calc.amounts[i])}</div>
                  {b.num < 4 && (
                    <div className="fbe-calc-row-yrs">{(calc.amounts[i] / expensesNum).toFixed(1)} yrs</div>
                  )}
                </div>
              ))}
            </div>
            <div className="fbe-calc-runway">
              Runway without touching B4: <strong>{calc.runway.toFixed(1)} years</strong>
              {calc.runway >= 7 ? ' ✓ crash-proof' : ' — consider adding more to B3'}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StepReady: React.FC<{ toolLink: (p: string) => string }> = ({ toolLink }) => (
  <div>
    <p className="fbe-label">Step 5 of 5</p>
    <h2 className="fbe-title">Full Example — Sarah's System</h2>
    <p className="fbe-intro">
      Age 63, retiring at 65 · $1,200,000 saved · $54,000/year core expenses ·
      Social Security at 67: $26,400/year
    </p>

    <div className="fbe-sarah-list">
      {[
        { ...BUCKETS[0], amount: 60000,  where: 'Marcus HYSA (4.5% APY)',                  coverage: '1.1 years of expenses',     extra: null },
        { ...BUCKETS[1], amount: 120000, where: 'SCHD ($80K) + Short bonds ($40K)',          coverage: '2.2 years of expenses',     extra: 'Annual dividends: ~$5,000' },
        { ...BUCKETS[2], amount: 324000, where: 'Balanced fund ($200K) + VIG ($124K)',       coverage: '6.0 years of expenses',     extra: null },
        { ...BUCKETS[3], amount: 696000, where: 'VOO ($500K) + VTI ($196K)',                 coverage: 'Funds years 10–30',         extra: 'At 8% avg/yr: ~$1.5M in 10 yrs' },
      ].map(b => (
        <div key={b.num} className="fbe-sarah-card" style={{ borderLeftColor: b.text }}>
          <div className="fbe-sarah-header">
            <span className="fbe-sarah-num" style={{ backgroundColor: b.text }}>{b.num}</span>
            <div>
              <span className="fbe-sarah-name" style={{ color: b.text }}>{b.name}</span>
              <span className="fbe-sarah-pct"> · {b.pct}%</span>
            </div>
            <span className="fbe-sarah-amt" style={{ color: b.text }}>{fmt(b.amount)}</span>
          </div>
          <div className="fbe-bar-track">
            <div className="fbe-bar-fill" style={{ width: `${b.pct * 1.5}%`, backgroundColor: b.text }} />
          </div>
          <div className="fbe-sarah-where">{b.where}</div>
          <div className="fbe-sarah-coverage">{b.coverage}</div>
          {b.extra && <div className="fbe-sarah-extra">{b.extra}</div>}
        </div>
      ))}
    </div>

    <div className="fbe-sarah-runway">
      <strong>Sarah's crash protection:</strong>{' '}
      1.1 + 2.2 + 6.0 = <span className="fbe-runway-highlight">9.3 years of runway</span> without touching B4 ✓
    </div>

    <div className="fbe-cta-grid">
      <div className="fbe-cta-card fbe-cta-card-primary">
        <div className="fbe-cta-icon">🪣</div>
        <h3>Bucket Visualizer</h3>
        <p>Enter your numbers and get personalized bucket allocation.</p>
        <Link to={toolLink('/bucket-visualizer')} className="fbe-btn fbe-btn-primary">
          Launch Visualizer →
        </Link>
      </div>
      <div className="fbe-cta-card">
        <div className="fbe-cta-icon">🔥</div>
        <h3>FIRE Calculator</h3>
        <p>Model your path to Financial Independence.</p>
        <Link to={toolLink('/fire-calculator')} className="fbe-btn fbe-btn-outline">
          Launch →
        </Link>
      </div>
      <div className="fbe-cta-card">
        <div className="fbe-cta-icon">📖</div>
        <h3>The Playbook</h3>
        <p>The complete 5 Pillars framework for building wealth.</p>
        <Link to={toolLink('/playbook')} className="fbe-btn fbe-btn-outline">
          View Playbook →
        </Link>
      </div>
    </div>

    <div className="fbe-cheatsheet">
      <div className="fbe-cheatsheet-title">The 4-Bucket Cheat Sheet</div>
      <div className="fbe-cheatsheet-rows">
        {[
          { lbl: 'B1 (5%)',  tag: 'Year 1 Cash',       inv: 'HYSA, Money Market',     color: '#276749', bg: '#D1FAE5' },
          { lbl: 'B2 (10%)', tag: 'Years 2–3 Buffer',  inv: 'SCHD, Short bonds, CDs', color: '#1e40af', bg: '#DBEAFE' },
          { lbl: 'B3 (27%)', tag: 'Years 4–10 Bridge', inv: 'Balanced funds, VIG',    color: '#92400e', bg: '#FEF3C7' },
          { lbl: 'B4 (58%)', tag: 'Years 10+ Growth',  inv: 'VOO, VTI',               color: '#5b21b6', bg: '#E9D8FD' },
        ].map(r => (
          <div key={r.lbl} className="fbe-cs-row" style={{ backgroundColor: r.bg }}>
            <span className="fbe-cs-label" style={{ color: r.color }}>{r.lbl}</span>
            <span className="fbe-cs-tag">{r.tag}</span>
            <span className="fbe-cs-arr">→</span>
            <span className="fbe-cs-inv">{r.inv}</span>
          </div>
        ))}
      </div>
      <div className="fbe-cheatsheet-protocol">
        <strong>Crash Protocol:</strong> Spend B1 → Refill from B2 → <strong>Protect B4</strong> → Wait
      </div>
    </div>
  </div>
);

/* ── Main Component ── */

export const FourBucketExplainer: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);

  const toolLink = (path: string) => (isAuthenticated ? path : '/login');

  const stepContent = [
    <StepWhat />,
    <StepWhy  />,
    <StepWhen />,
    <StepHow  />,
    <StepReady toolLink={toolLink} />,
  ];

  const goTo = (idx: number) => {
    setCurrentStep(idx);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={isAuthenticated ? 'dashboard-container' : ''}>
      {isAuthenticated && <Navigation />}
    <div className="fbe-page">

      {/* ── Nav (public only) ── */}
      {!isAuthenticated && (
      <nav className="fbe-nav">
        <Link to="/" className="fbe-nav-brand">
          <div className="fbe-nav-logo">W</div>
          <span className="fbe-nav-title">Wealth Financial Systems</span>
        </Link>
        <Link to="/login" className="fbe-nav-link">
          Sign In →
        </Link>
      </nav>
      )}

      {/* ── Hero ── */}
      <div className="fbe-hero">
        <div className="fbe-hero-badge">Retirement Strategy</div>
        <h1>The 4-Bucket System</h1>
        <p className="fbe-hero-sub">
          Never be forced to sell when the market crashes.
          A 5-step guide to protecting your retirement.
        </p>
      </div>

      {/* ── Wizard ── */}
      <div className="fbe-wizard">

        {/* Step indicator */}
        <div className="fbe-steps-bar">
          {STEPS.map((s, i) => (
            <div key={s.num} className="fbe-step-item">
              <button
                className={`fbe-step-btn ${currentStep === i ? 'active' : ''} ${currentStep > i ? 'done' : ''}`}
                onClick={() => goTo(i)}
              >
                <span className="fbe-step-num">{s.num}</span>
                <span className="fbe-step-label">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && <span className="fbe-step-arrow">›</span>}
            </div>
          ))}
        </div>

        {/* Current step content */}
        <div key={currentStep} className="fbe-step-content">
          {stepContent[currentStep]}
        </div>

        {/* Navigation */}
        <div className="fbe-wizard-nav">
          <span className="fbe-progress-text">
            {currentStep + 1} of {STEPS.length}
          </span>
          <div className="fbe-nav-btns">
            {currentStep > 0 && (
              <button className="fbe-btn fbe-btn-ghost" onClick={() => goTo(currentStep - 1)}>
                ← Back
              </button>
            )}
            {currentStep < STEPS.length - 1 ? (
              <button className="fbe-btn fbe-btn-primary" onClick={() => goTo(currentStep + 1)}>
                Next →
              </button>
            ) : (
              <Link to={toolLink('/bucket-visualizer')} className="fbe-btn fbe-btn-primary">
                Launch Visualizer →
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="fbe-footer">
        <p>
          Wealth Financial Systems · <Link to="/terms">Terms</Link> · <Link to="/privacy">Privacy</Link>
        </p>
        <p className="fbe-disclaimer">
          For educational purposes only. Not financial advice. Consult a qualified financial advisor before making investment decisions.
        </p>
      </footer>
    </div>
    </div>
  );
};
